import { useAuth0 } from '@auth0/auth0-react'
import { RouterProvider, createBrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { setAccessTokenGetter as setSessionTokenGetter } from '@api/services/sessionService'
import { setAccessTokenGetter as setV1TokenGetter, api } from '@api/v1/client'
import { initPendo, pendoEnabled } from '@/analytics/pendo'
import { resolvePendoIdentity } from '@/analytics/identity'
import Login from '@components/auth/Login'
import ProtectedRoute from '@components/auth/ProtectedRoute'
import SSOCallback from '@components/auth/SSOCallback'
import Layout from '@components/layout/Layout'
import { HOME_ROUTE } from '@config/featureFlags'
import Dashboard from '@pages/Dashboard'
import Matters from '@pages/Matters'
import MatterDetail from '@pages/MatterDetail'
import Contacts from '@pages/Contacts'
import TimeEntries from '@pages/TimeEntries'
import TimeSheet from '@pages/TimeSheet'
import Objects from '@pages/Objects'
import Billing from '@pages/Billing'
import Calendar from '@pages/Calendar'
import Documents from '@pages/Documents'
import Settings from '@pages/Settings'
import PreBills from '@pages/PreBills'
import PreBillDetail from '@pages/PreBillDetail'
import Reports from '@pages/Reports'
import ReportView from '@pages/ReportView'
import ExagoReportRoute from '@pages/reports/ExagoReportRoute'
import ReportQueue from '@pages/ReportQueue'
import ReportSchedules from '@pages/ReportSchedules'
import ReportPacks from '@pages/ReportPacks'
import Expenses from '@pages/Expenses'
import Payments from '@pages/Payments'
import PaymentDetail from '@pages/PaymentDetail'
import SecurityAdmin from '@pages/SecurityAdmin'
import RateTables from '@pages/RateTables'
import Accounting from '@pages/Accounting'
import InvoiceDetail from '@pages/InvoiceDetail'
import Invoices from '@pages/Invoices'
import TrustAccounting from '@pages/TrustAccounting'
import VendorBills from '@pages/VendorBills'
import BankReconciliation from '@pages/BankReconciliation'
import ContactDetail from '@pages/ContactDetail'
import BillGeneration from '@pages/BillGeneration'
import KanbanBoard from '@pages/KanbanBoard'
import BulkOperations from '@pages/BulkOperations'
import FormDesigner from '@pages/FormDesigner'
import Email from '@pages/Email'
import LoadingSpinner from '@components/common/LoadingSpinner'
import ToastContainer from '@components/common/ToastContainer'
import ErrorBoundary from '@components/common/ErrorBoundary'

/* eslint-disable no-undef */
console.log(`[CB2] version=${__APP_VERSION__} built=${__BUILD_TIME__}`)

/*
 * The whole route tree, unchanged, rendered under ONE splat route of a data router
 * (spec 158 review fix). A data router is what lets a page block in-app navigation
 * while something it cannot recall is in flight: react-router's useBlocker works only
 * under one, and Generate Bills uses it while bills are being generated. The splat
 * route is react-router's own way to move an app of <Routes> onto RouterProvider
 * without rewriting the tree.
 *
 * The ErrorBoundary inside keeps a page that throws on the app's own error screen: a
 * data router otherwise catches a render error itself and shows its built-in one.
 */
function AppRoutes() {
  return (
    <ErrorBoundary>
      <ToastContainer />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/ssocallback" element={<SSOCallback />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={HOME_ROUTE} replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="matters" element={<Matters />} />
          <Route path="matters/:id" element={<MatterDetail />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/:id" element={<ContactDetail />} />
          <Route path="time-entries" element={<TimeEntries />} />
          <Route path="time-sheet" element={<TimeSheet />} />
          <Route path="expenses" element={<Expenses />} />
          <Route path="billing" element={<Billing />} />
          <Route path="pre-bills" element={<PreBills />} />
          <Route path="pre-bills/:id" element={<PreBillDetail />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="documents" element={<Documents />} />
          <Route path="reports" element={<Reports />} />
          {/* Declared BEFORE reports/:reportId — otherwise "queue" is parsed as a
              report id and the queue renders as a broken report view. */}
          <Route path="reports/queue" element={<ReportQueue />} />
          <Route path="reports/queue/schedules" element={<ReportSchedules />} />
          <Route path="reports/queue/packs" element={<ReportPacks />} />
          {/* Spec 190: an Exago report run through CC-53794. Also before
              reports/:reportId, which would read "exago" as a report id.
              Spec 209: the route element picks spec 190's page or the generic one. */}
          <Route path="reports/exago/:exagoReportId" element={<ExagoReportRoute />} />
          <Route path="reports/:reportId" element={<ReportView />} />
          <Route path="payments" element={<Payments />} />
          <Route path="payments/:id" element={<PaymentDetail />} />
          <Route path="settings" element={<Settings />} />
          <Route path="security" element={<SecurityAdmin />} />
          <Route path="rate-tables" element={<RateTables />} />
          <Route path="accounting" element={<Accounting />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="generate-bills" element={<BillGeneration />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />
          <Route path="trust" element={<TrustAccounting />} />
          <Route path="vendor-bills" element={<VendorBills />} />
          <Route path="bank-reconciliation" element={<BankReconciliation />} />
          <Route path="kanban" element={<KanbanBoard />} />
          <Route path="bulk-operations" element={<BulkOperations />} />
          <Route path="form-designer" element={<FormDesigner />} />
          <Route path="email" element={<Email />} />
          <Route path="objects" element={<Objects />} />
          <Route path="objects/:objectType" element={<Objects />} />
          <Route path="objects/:objectType/:id" element={<Objects />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}

// One router for the page's lifetime, created on first use so that it reads the URL
// then. Module level, not component state: StrictMode runs a state initializer twice,
// and every browser router subscribes to popstate.
let appRouter = null
const getAppRouter = () => {
  if (!appRouter) appRouter = createBrowserRouter([{ path: '*', element: <AppRoutes /> }])
  return appRouter
}

function App() {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0()
  const [sessionChecked, setSessionChecked] = useState(false)

  useEffect(() => {
    setSessionTokenGetter(getAccessTokenSilently)
    setV1TokenGetter(getAccessTokenSilently)
  }, [getAccessTokenSilently])

  /*
   * Pendo identify (spec 094). No-op unless VITE_PENDO_API_KEY is set at build
   * time, so localhost and any non-beta deployment stay clean.
   *
   * Placed after the token-getter effect deliberately: `resolvePendoIdentity`
   * goes through `sessionService.getTokenClaims`, which needs the getter wired.
   * Effects run in declaration order within a commit, so that ordering holds.
   *
   * Deliberately NOT gated on `sessionChecked`, and deliberately not awaited:
   * analytics must never delay or block app boot, and it does not need a live
   * CB session to identify a visitor. Every failure path swallows.
   *
   * Idempotent by requirement, not by accident — `main.jsx` renders inside
   * React.StrictMode, so this effect double-invokes in dev. `initPendo` guards.
   */
  useEffect(() => {
    if (!isAuthenticated || !pendoEnabled()) return undefined
    let cancelled = false
    resolvePendoIdentity()
      .then((id) => {
        // A null identity means the token could not supply BOTH a tenant and a
        // CB user id. Do not initialize: an anonymous visitor is unattributable
        // noise that still consumes a Pendo seat.
        if (cancelled || !id) return
        initPendo(id)
        /*
         * `role` (CB StaffCategory) is fetched AFTER initialize, never before,
         * and is deliberately not awaited here (spec 097).
         *
         * The ordering is structural, not a timing bet: `initPendo` above has
         * already run to completion — it does no I/O, it reads the token — so
         * `pendo.initialize` cannot be delayed by this fetch no matter how slow
         * or broken `/users/me` is. The worst case is a visitor with no `role`,
         * which is exactly today's behaviour.
         *
         * Errors are swallowed, including a 500 from `/users/me`. A missing role
         * costs one segmentation dimension; it must never cost app boot or an
         * analytics session.
         */
        fetchPendoRole()
          .then((role) => { if (!cancelled && role) setVisitorRole(role) })
          .catch(() => { /* a role is a nice-to-have; never surface, never retry */ })
      })
      .catch(() => { /* analytics never blocks, and never surfaces */ })
    return () => { cancelled = true }
  }, [isAuthenticated])

  /*
   * There is deliberately no `pendo.updateOptions()` tenant-switch wiring here.
   *
   * The tracking plan says `setStoredSite()` lets a user change tenant on the
   * shared host without a new login, and asks for an in-place account update.
   * Re-resolved against this tree, that is not what the code does — there is no
   * SPA path on which `getTenant()` changes value without a full page load:
   *
   *   - `setStoredSite` has exactly ONE production call site,
   *     `components/auth/Login.jsx:131`, which is PRE-login and is immediately
   *     followed by an Auth0 redirect away from the page.
   *   - The only in-app switch is Header.jsx's "Switch site"
   *     (`components/layout/Header.jsx:212-229`), which clears the stored site
   *     and the Auth0 cache and then does `window.location.href = origin` — a
   *     hard navigation. The whole SPA reloads and this effect re-runs.
   *   - CC-53416 added two more ways to LEAVE a site — `/login?changesite=1`, the
   *     "Use a different site" link on the connecting screen, and clearing the
   *     site on Sign out. All three either end at the pre-login prompt or do a
   *     full page load, so none of them changes `getTenant()` in place either.
   *
   * So a fresh `initPendo` on boot already covers every tenant change, and an
   * `updateOptions` call here would be unreachable code claiming a capability.
   * `switchAccount` is exported from the module for the day an in-place switch
   * exists; nothing calls it today, and that is the honest state.
   */

  // Verify the CB backend session is alive when Auth0 says we're authenticated.
  // Auth0 tokens survive in localStorage long after the CB session expires.
  // If the API returns 401, the axios interceptor calls Auth0 logout (clears tokens + redirects).
  // The hanging promise keeps us on the loading spinner during that redirect.
  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      setSessionChecked(true)
      return
    }
    let cancelled = false
    api.get('/session/verify')
      .then(() => { if (!cancelled) setSessionChecked(true) })
      .catch(() => {
        // Non-auth errors (network, server down) — let the app render anyway
        if (!cancelled) setSessionChecked(true)
      })
    return () => { cancelled = true }
  }, [isLoading, isAuthenticated])

  if (isLoading || (isAuthenticated && !sessionChecked)) {
    return <LoadingSpinner fullScreen message="Loading Centerbase..." />
  }

  return (
    <ErrorBoundary>
      <RouterProvider router={getAppRouter()} />
    </ErrorBoundary>
  )
}

export default App
